import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useScope } from "../context/ScopeContext";
import { useI18n } from "../i18n";
import { btn, selectCls, label, PageTitle } from "../lib/ui";
import { fmtDate } from "../lib/format";
import { FileSpreadsheet, FileText, FileDown, Mail, CalendarClock } from "lucide-react";

export default function Exports() {
  const { user } = useAuth();
  const { scope } = useScope();
  const { t, locale } = useI18n();
  const MR = ({
    es: { title: "Informe mensual", desc: "Descarga el informe de un mes concreto (ingresos, gastos y resultado) en PDF, Excel o CSV.", month: "Mes" },
    en: { title: "Monthly report", desc: "Download the report for a specific month (income, expenses and result) in PDF, Excel or CSV.", month: "Month" },
    fr: { title: "Rapport mensuel", desc: "Téléchargez le rapport d'un mois précis (revenus, dépenses et résultat) en PDF, Excel ou CSV.", month: "Mois" },
    pt: { title: "Relatório mensal", desc: "Descarregue o relatório de um mês específico (receitas, despesas e resultado) em PDF, Excel ou CSV.", month: "Mês" },
    it: { title: "Report mensile", desc: "Scarica il report di un mese specifico (entrate, spese e risultato) in PDF, Excel o CSV.", month: "Mese" },
  })[locale] || { title: "Informe mensual", desc: "Descarga el informe de un mes concreto (ingresos, gastos y resultado) en PDF, Excel o CSV.", month: "Mes" };
  const readOnly = user?.role === "accountant";
  const [expScope, setExpScope] = useState("all");
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [history, setHistory] = useState([]);
  const [emails, setEmails] = useState([]);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [canMonthly, setCanMonthly] = useState(false);

  const load = async () => {
    try {
      const [h, e, f] = await Promise.all([api.get("/exports"), api.get("/emails"), api.get("/feature-flags")]);
      setHistory(h.data); setEmails(e.data);
      setCanMonthly(!!f.data?.flags?.monthly_reports);
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { setExpScope(scope); }, [scope]);
  useEffect(() => { load(); }, []);

  const qs = () => {
    const p = new URLSearchParams();
    p.set("lang", locale);
    if (expScope !== "all") p.set("scope", expScope);
    if (range.date_from) p.set("date_from", range.date_from);
    if (range.date_to) p.set("date_to", range.date_to);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const doExport = async (fmt, ext) => {
    setBusy(true);
    try { await downloadFile(`/exports/transactions.${fmt}${qs()}`, `lura_movimientos.${ext}`); toast.success(t("ex.exportReady").replace("{ext}", ext.toUpperCase())); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const weekly = async () => {
    try { const { data } = await api.post("/summary/weekly"); toast.success(t("ex.weeklyQueued").replace("{r}", data.result)); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doMonthly = async (fmt, ext) => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      p.set("month", month); p.set("lang", locale);
      if (expScope !== "all") p.set("scope", expScope);
      await downloadFile(`/reports/monthly.${fmt}?${p.toString()}`, `lura_informe_${month}.${ext}`);
      toast.success(t("ex.exportReady").replace("{ext}", ext.toUpperCase())); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageTitle title={t("p.exports.t")} subtitle={t("p.exports.s")} />

      <div className="bg-white border border-[#D5E7E5] rounded-lg p-6 mb-6">
        <div className="grid sm:grid-cols-3 gap-4 mb-5">
          <div><label className={label}>{t("ex.scope")}</label>
            <select value={expScope} onChange={(e) => setExpScope(e.target.value)} className={selectCls} data-testid="export-scope"><option value="all">{t("ex.consolidated")}</option><option value="professional">{t("ex.professional")}</option><option value="personal">{t("ex.personal")}</option></select></div>
          <div><label className={label}>{t("ex.from")}</label><input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="w-full h-10 px-3 rounded-md border border-[#D5E7E5] bg-white text-sm" /></div>
          <div><label className={label}>{t("ex.to")}</label><input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="w-full h-10 px-3 rounded-md border border-[#D5E7E5] bg-white text-sm" /></div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => doExport("csv", "csv")} disabled={busy} className={btn.outline} data-testid="button-export-csv"><FileDown size={16} /> CSV</button>
          <button onClick={() => doExport("xlsx", "xlsx")} disabled={busy} className={btn.outline} data-testid="button-export-xlsx"><FileSpreadsheet size={16} /> Excel</button>
          <button onClick={() => doExport("pdf", "pdf")} disabled={busy} className={btn.accent} data-testid="button-export-pdf"><FileText size={16} /> PDF</button>
          {!readOnly && <button onClick={weekly} className={btn.primary} data-testid="button-weekly-summary"><CalendarClock size={16} /> {t("ex.weekly")}</button>}
        </div>
      </div>

      {canMonthly && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-6 mb-6" data-testid="monthly-report-card">
          <div className="flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-[#0299A0]" /><h3 className="text-lg font-medium text-[#075E62]">{MR.title}</h3></div>
          <p className="text-xs text-[#5F8588] mb-4">{MR.desc}</p>
          <div className="flex flex-wrap items-end gap-3">
            <div><label className={label}>{MR.month}</label><input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-month" className="h-10 px-3 rounded-md border border-[#D5E7E5] bg-white text-sm" /></div>
            <button onClick={() => doMonthly("csv", "csv")} disabled={busy} className={btn.outline} data-testid="button-monthly-csv"><FileDown size={16} /> CSV</button>
            <button onClick={() => doMonthly("xlsx", "xlsx")} disabled={busy} className={btn.outline} data-testid="button-monthly-xlsx"><FileSpreadsheet size={16} /> Excel</button>
            <button onClick={() => doMonthly("pdf", "pdf")} disabled={busy} className={btn.accent} data-testid="button-monthly-pdf"><FileText size={16} /> PDF</button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-6">
          <h3 className="text-lg font-medium text-[#075E62] mb-4">{t("ex.history")}</h3>
          {history.length === 0 ? <p className="text-sm text-[#5F8588]">{t("ex.noExports")}</p> : (
            <div className="flex flex-col divide-y divide-[#D5E7E5]">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-[#075E62] uppercase font-medium">{h.format}</span>
                  <span className="text-[#5F8588]">{h.scope === "all" ? t("ex.consolidated") : h.scope === "professional" ? t("ex.professional") : t("ex.personal")} · {h.rows} {t("ex.rows")}</span>
                  <span className="text-[#5F8588]">{fmtDate(h.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#D5E7E5] rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4"><Mail size={18} className="text-[#0299A0]" /><h3 className="text-lg font-medium text-[#075E62]">{t("ex.emails")}</h3></div>
          <p className="text-xs text-[#5F8588] mb-3">{t("ex.emailsNote")}</p>
          {emails.length === 0 ? <p className="text-sm text-[#5F8588]">{t("ex.noEmails")}</p> : (
            <div className="flex flex-col divide-y divide-[#D5E7E5] max-h-80 overflow-y-auto">
              {emails.map((m) => (
                <div key={m.id} className="py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#075E62] font-medium truncate">{m.subject}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#D5E7E5]/40 text-[#5F8588]">{m.status}</span>
                  </div>
                  <p className="text-xs text-[#5F8588] mt-0.5">{m.to} · {m.kind} · {fmtDate(m.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
